import { getCompletedUploadUrl, getQueuedUploadBlob } from "../data/uploadQueue";
import { Capacitor } from "@capacitor/core";
import { fetchGooglePlacePhotos } from "../components/PlacePicker";
import { Directory, Filesystem } from "@capacitor/filesystem";
import {
  exportTripWorkspaceSection,
  listTrips,
  type TripWorkspaceSectionId,
  type TripSummary,
  type TripDocumentItem,
  type TripTicketItem,
  type TripPhotoItem
} from "./tripStore";

/** A read-only, account-scoped snapshot. It never hydrates the live trip store or syncs to Firebase. */
export const OFFLINE_SCHEMA_VERSION = 1;
const DB_NAME = "mundely-trip-offline-v1";
const STORE = "travel";
const FILE_LIMIT = 20 * 1024 * 1024;
const PACKAGE_LIMIT = 150 * 1024 * 1024;

export const offlineSections = [
  "itinerary", "boardingPasses", "tickets", "hotels", "restaurants", "places",
  "pointsOfInterest", "experiences", "events", "usefulInfo", "climate",
  "checklists", "notes", "documents", "photos", "expenses"
] as const satisfies readonly TripWorkspaceSectionId[];

export type OfflineSection = (typeof offlineSections)[number];
export type OfflineFileChoice = { key: string; name: string; mimeType: string; url?: string; queueId?: string; size: number };
export type OfflineStoredFile = { key: string; name: string; mimeType: string; blob: Blob; sourceVersion?: string };
const OFFLINE_VISUAL_KEYS = { cover: "trip-visual:cover", profile: "trip-visual:profile" } as const;
export type OfflineTripVisualKind = keyof typeof OFFLINE_VISUAL_KEYS;
export interface OfflineTripPackage {
  key: string;
  uid: string;
  tripId: string;
  trip: TripSummary;
  createdAt: string;
  version: number;
  workspace: Record<OfflineSection, unknown>;
  files: OfflineStoredFile[];
  totalBytes: number;
  pendingFiles?: string[];
  selectedKeys?: string[];
  knownFileKeys?: string[];
  includePhotos?: boolean;
}

function updated(uid: string, tripId: string): void {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("mundely:offline-updated", { detail: { uid, tripId } }));
}

function packageKey(uid: string, tripId: string): string {
  if (!uid || uid === "anonymous" || !tripId) throw new Error("offline/no-account");
  return `${uid}:${tripId}`;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("offline/storage-not-available")); return; }
    const request = indexedDB.open(DB_NAME, OFFLINE_SCHEMA_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("offline/storage-error"));
    request.onblocked = () => reject(new Error("offline/storage-blocked"));
  });
}

async function transact<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDB();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = fn(transaction.objectStore(STORE));
      let result: T;
      request.onsuccess = () => { result = request.result; };
      request.onerror = () => reject(request.error || new Error("offline/storage-error"));
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error || new Error("offline/storage-error"));
      transaction.onabort = () => reject(transaction.error || new Error("offline/storage-aborted"));
    });
  } finally { db.close(); }
}

export async function getOfflineTrip(uid: string, tripId: string): Promise<OfflineTripPackage | null> {
  const found = await transact<OfflineTripPackage | undefined>("readonly", store => store.get(packageKey(uid, tripId)));
  return found && found.uid === uid && found.tripId === tripId && found.version === OFFLINE_SCHEMA_VERSION ? found : null;
}

export async function deleteOfflineTrip(uid: string, tripId: string): Promise<void> {
  await transact<undefined>("readwrite", store => store.delete(packageKey(uid, tripId)));
  updated(uid, tripId);
}

export function offlineDocumentChoices(uid: string, tripId: string): OfflineFileChoice[] {
  const documents = exportTripWorkspaceSection(uid, tripId, "documents") as { items?: TripDocumentItem[] } | null;
  const tickets = exportTripWorkspaceSection(uid, tripId, "tickets") as TripTicketItem[] | null;
  return [
    ...(documents?.items || []).map(item => ({ key: `document:${item.id}`, name: item.name, mimeType: item.mimeType, url: item.url, queueId: item.queueId, size: item.size })),
    ...(tickets || []).map(item => ({ key: `ticket:${item.id}`, name: item.name, mimeType: item.mimeType, url: item.url, queueId: item.queueId, size: item.size }))
  ];
}

/** Album photos are opt-in. Ticket/document files are included in both modes. */
export function offlinePhotoChoices(uid: string, tripId: string): OfflineFileChoice[] {
  const photos = exportTripWorkspaceSection(uid, tripId, "photos") as TripPhotoItem[] | null;
  return (photos || []).map(item => ({
    key: `photo:${item.id}`, name: item.name, mimeType: "image/jpeg",
    url: item.url, queueId: item.queueId, size: 0
  }));
}
export function offlineAttachmentChoices(uid: string, tripId: string, includePhotos: boolean): OfflineFileChoice[] {
  return [...offlineDocumentChoices(uid, tripId), ...(includePhotos ? offlinePhotoChoices(uid, tripId) : [])];
}
export function estimateOfflineBytes(uid: string, trip: TripSummary, includePhotos: boolean): number {
  // Album originals generally do not expose their file size: estimate, never promise exact bytes.
  const data = JSON.stringify({ trip, workspace: getWorkspace(uid, trip.id, includePhotos) });
  const known = offlineDocumentChoices(uid, trip.id).reduce((sum, file) => sum + Math.max(0, file.size || 0), 0);
  return new Blob([data]).size + known + (includePhotos ? offlinePhotoChoices(uid, trip.id).length * 1048576 : 0);
}

/** Prefer the original queued bytes; native downloads bypass WebView CORS for Firebase signed URLs. */
async function downloadBlob(url: string, signal: AbortSignal): Promise<Blob> {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:") throw new Error("offline/unsafe-url");
  if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
  if (Capacitor.isNativePlatform()) {
    const path = `mundely-offline-${crypto.randomUUID()}`;
    try {
      await Filesystem.downloadFile({ url: parsed.href, path, directory: Directory.Cache, progress: false });
      if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
      if ((await Filesystem.stat({ path, directory: Directory.Cache })).size > FILE_LIMIT) throw new Error("offline/file-too-large");
      const file = await Filesystem.readFile({ path, directory: Directory.Cache });
      if (typeof file.data !== "string") return file.data;
      const bytes = Uint8Array.from(atob(file.data), ch => ch.charCodeAt(0));
      if (bytes.byteLength > FILE_LIMIT) throw new Error("offline/file-too-large");
      return new Blob([bytes.buffer as ArrayBuffer], { type: "application/octet-stream" });
    } finally {
      await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    }
  }
  const response = await fetch(parsed.href, { signal, credentials: "omit", cache: "no-store" });
  if (!response.ok || !response.body) throw new Error("offline/file-not-available");
  const declared = Number(response.headers.get("content-length") || "0");
  if (declared > FILE_LIMIT) throw new Error("offline/file-too-large");
  const reader = response.body.getReader();
  const parts: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > FILE_LIMIT) throw new Error("offline/file-too-large");
      parts.push(value);
    }
  } finally { reader.releaseLock(); }
  const combined = new ArrayBuffer(size);
  const destination = new Uint8Array(combined);
  let offset = 0;
  for (const part of parts) { destination.set(part, offset); offset += part.byteLength; }
  return new Blob([combined], { type: response.headers.get("content-type") || "application/octet-stream" });
}

async function resolveAttachment(choice: OfflineFileChoice, signal: AbortSignal): Promise<Blob> {
  if (choice.queueId) {
    const local = await getQueuedUploadBlob(choice.queueId).catch(() => null);
    if (local) return local;
  }
  const receipt = choice.queueId ? await getCompletedUploadUrl(choice.queueId).catch(() => null) : null;
  const url = receipt || choice.url;
  if (!url) throw new Error("offline/file-not-uploaded");
  try { return await downloadBlob(url, signal); }
  catch (error) {
    if (signal.aborted) throw error;
    throw new Error(`offline/file-not-available:${choice.name}`);
  }
}

function fileVersion(choice: OfflineFileChoice): string {
  return [choice.queueId || "", choice.url || "", choice.size].join("|");
}

function visualSourceVersion(trip: TripSummary, kind: OfflineTripVisualKind): string {
  const prefix = kind === "cover" ? "cover" : "profile";
  const dataUrl = prefix === "cover" ? trip.coverPhotoDataUrl : trip.profilePhotoDataUrl;
  const placeId = prefix === "cover" ? trip.coverPhotoGooglePlaceId : trip.profilePhotoGooglePlaceId;
  const photoIndex = prefix === "cover" ? trip.coverPhotoIndex : trip.profilePhotoIndex;
  return [kind, dataUrl || "", placeId || "", Number(photoIndex || 0)].join("|");
}

async function dataUrlBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) throw new Error("offline/file-not-available");
  return response.blob();
}

async function resolveTripVisualFile(trip: TripSummary, kind: OfflineTripVisualKind, signal: AbortSignal): Promise<OfflineStoredFile | null> {
  const useCover = kind === "cover";
  const dataUrl = useCover ? trip.coverPhotoDataUrl : trip.profilePhotoDataUrl;
  const placeId = useCover ? trip.coverPhotoGooglePlaceId : trip.profilePhotoGooglePlaceId;
  const photoIndex = Math.max(0, Number(useCover ? trip.coverPhotoIndex : trip.profilePhotoIndex) || 0);
  if (!dataUrl && !placeId) return null;
  let blob: Blob | null = null;
  if (dataUrl) {
    blob = await dataUrlBlob(dataUrl);
  } else if (placeId) {
    const photos = await fetchGooglePlacePhotos(placeId, Math.max(1, photoIndex + 1));
    const selected = photos[Math.min(photoIndex, Math.max(0, photos.length - 1))];
    if (!selected?.url) return null;
    blob = await downloadBlob(selected.url, signal);
  }
  if (!blob) return null;
  if (blob.size > FILE_LIMIT) throw new Error("offline/file-too-large");
  return {
    key: OFFLINE_VISUAL_KEYS[kind],
    name: kind === "cover" ? "cover-photo.jpg" : "profile-photo.jpg",
    mimeType: blob.type || "image/jpeg",
    blob,
    sourceVersion: visualSourceVersion(trip, kind)
  };
}

async function collectTripVisualFiles(trip: TripSummary, signal: AbortSignal, existing: Map<string, OfflineStoredFile>): Promise<{ files: OfflineStoredFile[]; pending: string[] }> {
  const files: OfflineStoredFile[] = [];
  const pending: string[] = [];
  for (const kind of ["cover", "profile"] as const) {
    const hasSource = kind === "cover"
      ? Boolean(trip.coverPhotoDataUrl || trip.coverPhotoGooglePlaceId)
      : Boolean(trip.profilePhotoDataUrl || trip.profilePhotoGooglePlaceId);
    if (!hasSource) continue;
    const sourceVersion = visualSourceVersion(trip, kind);
    const key = OFFLINE_VISUAL_KEYS[kind];
    const previous = existing.get(key);
    if (previous && previous.sourceVersion === sourceVersion) {
      files.push(previous);
      continue;
    }
    try {
      const resolved = await resolveTripVisualFile(trip, kind, signal);
      if (resolved) files.push(resolved);
      else if (previous) files.push(previous);
    } catch (error) {
      if (signal.aborted) throw error;
      if (previous) files.push(previous);
      pending.push(kind === "cover" ? "cover" : "profile");
    }
  }
  return { files, pending };
}

function getWorkspace(uid: string, tripId: string, includePhotos = false): Record<OfflineSection, unknown> {
  const workspace = {} as Record<OfflineSection, unknown>;
  for (const section of offlineSections) workspace[section] = section === "photos" && !includePhotos ? [] : exportTripWorkspaceSection(uid, tripId, section);
  return workspace;
}

function snapshotBytes(trip: TripSummary, workspace: Record<OfflineSection, unknown>, files: OfflineStoredFile[]): number {
  return new Blob([JSON.stringify({ trip, workspace })]).size + files.reduce((sum, file) => sum + file.blob.size, 0);
}

/** Only selected attachments are copied; any failure leaves the previous snapshot untouched. */
export async function saveTripOffline(
  uid: string,
  trip: TripSummary,
  selectedFileKeys: readonly string[],
  progress: (done: number, total: number, name: string) => void,
  signal: AbortSignal,
  requireExisting = false,
  includePhotos = false
): Promise<OfflineTripPackage> {
  const key = packageKey(uid, trip.id);
  const choices = offlineAttachmentChoices(uid, trip.id, includePhotos);
  const requested = new Set(selectedFileKeys);
  if (requested.size !== selectedFileKeys.length || choices.filter(item => requested.has(item.key)).length !== requested.size) {
    throw new Error("offline/invalid-selection");
  }
  const selected = choices.filter(item => requested.has(item.key));
  const prior = await getOfflineTrip(uid, trip.id);
  const existing = new Map((prior?.files || []).map(item => [item.key, item]));
  const workspace = getWorkspace(uid, trip.id, includePhotos);
  const files: OfflineStoredFile[] = [];
  const pendingFiles: string[] = [];
  progress(0, selected.length + 1, "");
  for (let index = 0; index < selected.length; index++) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
    const choice = selected[index];
    const previous = existing.get(choice.key);
    progress(index, selected.length + 1, choice.name);
    if (previous && previous.sourceVersion === fileVersion(choice)) {
      files.push(previous);
    } else {
      try {
        if (choice.size > FILE_LIMIT) throw new Error("offline/file-too-large");
        const blob = await resolveAttachment(choice, signal);
        if (!blob.size) throw new Error("offline/empty-file");
        if (blob.size > FILE_LIMIT) throw new Error("offline/file-too-large");
        files.push({ key: choice.key, name: choice.name, mimeType: choice.mimeType || blob.type, blob, sourceVersion: fileVersion(choice) });
      } catch (error) {
        if (signal.aborted) throw error;
        if (previous) files.push(previous); // Never discard the last usable attachment.
        pendingFiles.push(choice.name);
      }
    }
    progress(index + 1, selected.length + 1, choice.name);
  }
  if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
  const visualFiles = await collectTripVisualFiles(trip, signal, existing);
  for (const item of visualFiles.files) {
    const index = files.findIndex((current) => current.key === item.key);
    if (index >= 0) files[index] = item; else files.push(item);
  }
  pendingFiles.push(...visualFiles.pending);
  const totalBytes = snapshotBytes(trip, workspace, files);
  if (totalBytes > PACKAGE_LIMIT) throw new Error("offline/package-too-large");
  const snapshot: OfflineTripPackage = {
    key, uid, tripId: trip.id, trip: { ...trip }, createdAt: new Date().toISOString(),
    version: OFFLINE_SCHEMA_VERSION, workspace, files, totalBytes, pendingFiles,
    selectedKeys: [...requested], knownFileKeys: choices.map(item => item.key), includePhotos
  };
  if (requireExisting && !(await getOfflineTrip(uid, trip.id))) throw new Error("offline/not-enabled");
  await transact<IDBValidKey>("readwrite", store => store.put(snapshot));
  progress(selected.length + 1, selected.length + 1, "");
  updated(uid, trip.id);
  return snapshot;
}

/** Every persisted change updates only an already-opted-in snapshot; never writes to live trips. */
async function refreshStoredSnapshot(uid: string, tripId: string): Promise<void> {
  const previous = await getOfflineTrip(uid, tripId);
  if (!previous) return;
  const trip = listTrips(uid).find(item => item.id === tripId);
  if (!trip) return;
  const workspace = getWorkspace(uid, tripId, Boolean(previous.includePhotos));
  // Keep captured documents when offline; the current metadata stays available immediately.
  const currentFileKeys = offlineAttachmentChoices(uid, tripId, Boolean(previous.includePhotos)).map(item => item.key);
  // A user opted in to the whole trip: retain all documents and all opted-in photos.
  // This also upgrades snapshots created when individual file selection was required.
  const selectedKeys = [...currentFileKeys];
  const visualStale = (["cover", "profile"] as const).some(kind => {
    const enabled = kind === "cover" ? Boolean(trip.coverPhotoDataUrl || trip.coverPhotoGooglePlaceId) : Boolean(trip.profilePhotoDataUrl || trip.profilePhotoGooglePlaceId);
    return enabled && previous.files.find(file => file.key === OFFLINE_VISUAL_KEYS[kind])?.sourceVersion !== visualSourceVersion(trip, kind);
  });
  const same = JSON.stringify({ trip, workspace, selectedKeys, knownFileKeys: currentFileKeys }) === JSON.stringify({ trip: previous.trip, workspace: previous.workspace, selectedKeys: previous.selectedKeys || previous.files.map(item => item.key), knownFileKeys: previous.knownFileKeys || currentFileKeys });
  if (!same) {
    const totalBytes = snapshotBytes(trip, workspace, previous.files);
    if (totalBytes > PACKAGE_LIMIT) return;
    if (!(await getOfflineTrip(uid, tripId))) return; // Do not revive an intentionally deleted copy.
    await transact<IDBValidKey>("readwrite", store => store.put({ ...previous, trip: { ...trip }, workspace, totalBytes, selectedKeys, knownFileKeys: currentFileKeys, createdAt: new Date().toISOString() }));
    updated(uid, tripId);
  }
  {
    const choices = offlineAttachmentChoices(uid, tripId, Boolean(previous.includePhotos));
    const stored = new Map(previous.files.map(item => [item.key, item]));
    const needed = choices.filter(choice => selectedKeys.includes(choice.key) && stored.get(choice.key)?.sourceVersion !== fileVersion(choice));
    if (needed.length || visualStale) {
      // Files fail independently, while metadata and existing files remain usable.
      const controller = new AbortController();
      await saveTripOffline(uid, trip, selectedKeys.filter(key => choices.some(item => item.key === key)), () => undefined, controller.signal, true, Boolean(previous.includePhotos));
    }
  }
}

/** Debounced, serialized, event-driven updates. No 5-second polling or network requirement for metadata. */
export function startOfflineAutoUpdates(uid: string): () => void {
  const timers = new Map<string, number>();
  const running = new Map<string, Promise<void>>();
  const schedule = (tripId: string) => {
    if (!tripId) return;
    const last = timers.get(tripId);
    if (last) window.clearTimeout(last);
    timers.set(tripId, window.setTimeout(() => {
      timers.delete(tripId);
      const previous = running.get(tripId) || Promise.resolve();
      const next = previous.catch(() => undefined).then(() => refreshStoredSnapshot(uid, tripId));
      running.set(tripId, next);
      void next.catch(() => undefined).finally(() => { if (running.get(tripId) === next) running.delete(tripId); });
    }, 850));
  };
  const listen = (event: Event) => {
    const detail = (event as CustomEvent<{ uid?: string; tripId?: string }>).detail;
    if (detail?.uid && detail.uid !== uid) return;
    if (detail?.tripId) schedule(detail.tripId);
    else for (const trip of listTrips(uid)) schedule(trip.id);
  };
  const onOnline = () => { for (const trip of listTrips(uid)) schedule(trip.id); };
  for (const event of ["tripai:workspace-updated", "tripai:remote-workspace-updated", "tripai:trips-updated"]) window.addEventListener(event, listen);
  window.addEventListener("online", onOnline);
  return () => {
    for (const timer of timers.values()) window.clearTimeout(timer);
    for (const event of ["tripai:workspace-updated", "tripai:remote-workspace-updated", "tripai:trips-updated"]) window.removeEventListener(event, listen);
    window.removeEventListener("online", onOnline);
  };
}

/** Cached originals for the ordinary document screen (no duplicate trip reader). */
export async function getOfflineDocumentBlob(uid: string, tripId: string, documentId: string): Promise<Blob | null> {
  const snapshot = await getOfflineTrip(uid, tripId);
  return snapshot?.files.find(file => file.key === `document:${documentId}`)?.blob || null;
}

export async function getOfflinePhotoBlob(uid: string, tripId: string, photoId: string): Promise<Blob | null> {
  const snapshot = await getOfflineTrip(uid, tripId);
  if (!snapshot?.includePhotos) return null;
  return snapshot.files.find(file => file.key === `photo:${photoId}`)?.blob || null;
}

export async function getOfflineTripVisualBlob(uid: string, tripId: string, kind: OfflineTripVisualKind): Promise<Blob | null> {
  const snapshot = await getOfflineTrip(uid, tripId);
  return snapshot?.files.find(file => file.key === OFFLINE_VISUAL_KEYS[kind])?.blob || null;
}
